import { GridButtonsV2 } from "@/controls/buttons/GridButtonsV2";
import { CommentsColumn } from "@/controls/ag-grid/components/CommentsColumn";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { Charge } from "@/pages/sy/models/Charge";
import { ChargeStatusTag } from "@/pages/sy/components/ChargeStatusTag";
import { getChargeStatusLabel } from "@/pages/sy/constants/chargeStatus";
import { get } from "lodash-es";

const cellStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

type Props = {
  setCharge: (charge: Charge) => void;
  setShowModal: (show: "new" | "edit" | "delete" | "") => void;
  charges: Charge[] | null;
  setShowCommentModal?: (
    show: boolean,
    charge?: Charge,
    position?: { top: number; left: number },
  ) => void;
};

export const useTableData = ({
  setCharge,
  setShowModal,
  charges,
  setShowCommentModal,
}: Props) => {
  const columnDefs: ColDef[] = [
    {
      field: "BucketName",
      headerName: "Bucket",
      minWidth: 60,
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: "ChargeStatusCode",
      headerName: "Status",
      minWidth: 60,
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
      cellRenderer: (params: ICellRendererParams) => {
        return <ChargeStatusTag code={params.value} />;
      },
    },
    {
      field: "StatusText",
      headerName: "Status Text",
      hide: true,
      suppressColumnsToolPanel: true,
      minWidth: 60,
      flex: 1,
      cellStyle: cellStyle,
      valueGetter: (params) =>
        getChargeStatusLabel(params.data.ChargeStatusCode),
    },
    {
      field: "ScheduledHeatName",
      headerName: "Scheduled",
      minWidth: 80,
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: "HeatName",
      headerName: "Charged",
      minWidth: 60,
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: "RecipeName",
      headerName: "Recipe",
      minWidth: 60,
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: "ChargeNumber",
      headerName: "#",
      minWidth: 20,
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: "OpenTimestamp",
      headerName: "Open",
      minWidth: 80,
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data.OpenTimestamp, "MM/dd HH:mm"),
    },
    {
      field: "CloseTimestamp",
      headerName: "Close",
      minWidth: 80,
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data.CloseTimestamp, "MM/dd HH:mm"),
    },
    {
      field: "ManualTargetWeight",
      headerName: "Target",
      flex: 1,
      minWidth: 60,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
      valueGetter: (params) => get(params, "data.ManualTargetWeight", "-"),
    },
    {
      field: "OperatorName",
      headerName: "Operator",
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
      hide: true,
      suppressColumnsToolPanel: false,
    },
    {
      field: "TotalWeight",
      headerName: "Loaded",
      flex: 1,
      cellStyle: (params) => {
        const loadedWeight =
          parseFloat(get(params, "data.TotalWeight", "0")) || 0;
        const targetWeight =
          parseFloat(get(params, "data.ManualTargetWeight", "0")) || 0;
        if (loadedWeight > targetWeight) {
          return {
            ...cellStyle,
            color: "error.main",
          };
        }
        return cellStyle;
      },
      minWidth: 60,
      wrapText: true,
      autoHeight: true,
      valueGetter: (params) => get(params, "data.TotalWeight", "0"),
    },
    {
      field: "Comments",
      headerName: "Notes",
      flex: 1,
      cellStyle: cellStyle,
      wrapText: true,
      autoHeight: true,
      minWidth: 60,
      cellRenderer: (params: ICellRendererParams) => {
        if (!setShowCommentModal) return null;
        return (
          <CommentsColumn
            data={params?.data}
            setShowOpenModal={setShowCommentModal}
          />
        );
      },
      valueGetter: (params) => params.data?.Comments || "",
    },
    {
      field: "-",
      cellRenderer: GridButtonsV2,
      cellRendererParams: (params: ICellRendererParams) => ({
        setData: setCharge,
        data: params.data as Charge,
        setShowModal,
        edit: false,
      }),
      wrapText: false,
      autoHeight: false,
      wrapHeaderText: false,
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 40,
      maxWidth: 60,
      filter: false,
      autoHeaderHeight: true,
    },
  ];
  return { columnDefs };
};
